import { useEffect, useState } from "react";
import { useImmer } from "use-immer";
import { createPortal } from "react-dom";

import type { AgenticLogicRuleRunListItemDto } from "@intelligems/ig-types";
import { IgAlert } from "@intelligems/ig-theme";
import Stack from "@mui/material/Stack";

import {
  useStartAgenticLogicRuleRunsListen,
  useStopAgenticLogicRuleRunsListen,
} from "@src/api/agentic-logic/agentic-logic-rule-runs-listen";
import { useAgenticLogicRuleRuns } from "@src/api/agentic-logic/list-agentic-logic-rule-runs";
import { LIVE_RUNS_POLL_INTERVAL_MS } from "@src/features/agenticCodegen/workspace/live-runs/constants";
import { LiveRunsListenControl } from "@src/features/agenticCodegen/workspace/live-runs/LiveRunsListenControl";
import { useLiveRunsListen } from "@src/features/agenticCodegen/workspace/live-runs/useLiveRunsListen";

import { RecentRunsGrid } from "./RecentRunsGrid";
import { RunDetailDialog } from "./RunDetailDialog";

interface RecentRunsPanelProps {
  ruleId: string;
}

/**
 * The Intelligems-only "Recent runs" body of a rule's Advanced tab: recorded
 * executions in a grid, and a detail dialog for the one clicked. Mount it
 * only behind the admin gate — the list query fires on mount and the route
 * refuses a member token.
 *
 * Opening the panel opens a live-listen window for the rule: while it is
 * open the serving broker records every evaluation (with the rule's console
 * output) to a short-lived store that is polled here and shown ahead of the
 * recorded history, deduped by invocation.
 */
export const RecentRunsPanel = ({ ruleId }: RecentRunsPanelProps) => {
  const [selected, setSelected] =
    useImmer<AgenticLogicRuleRunListItemDto | null>(null);
  const start = useStartAgenticLogicRuleRunsListen();
  const stop = useStopAgenticLogicRuleRunsListen();
  // The listing's window fact feeds the hook through a one-render mirror: the
  // live query's poll interval depends on the hook's `isListening`, so the
  // two cannot reference each other in the same render.
  const [serverListenWindowExpiresAt, setServerListenWindowExpiresAt] =
    useState<number | null | undefined>(undefined);
  const listen = useLiveRunsListen({
    start: (windowMinutes) =>
      start.mutateAsync({ pathParams: { ruleId }, body: { windowMinutes } }),
    stop: () => stop.mutateAsync({ pathParams: { ruleId } }),
    serverListenWindowExpiresAt,
    // Opening the panel starts listening; leaving it stops. The store's TTL
    // is the backstop if the DELETE never lands.
    autoStart: true,
    resetKey: ruleId,
  });
  const history = useAgenticLogicRuleRuns(ruleId, { source: "history" });
  const live = useAgenticLogicRuleRuns(ruleId, {
    source: "live",
    refetchInterval: listen.isListening ? LIVE_RUNS_POLL_INTERVAL_MS : false,
  });
  useEffect(() => {
    if (live.data)
      setServerListenWindowExpiresAt(live.data.listenWindowExpiresAt);
  }, [live.data]);

  const liveRuns = live.data?.runs ?? [];
  const liveIds = new Set(liveRuns.map((run) => run.invocationId));
  const runs = [
    ...liveRuns,
    ...(history.data?.runs ?? []).filter(
      (run) => !liveIds.has(run.invocationId)
    ),
  ];
  const error = history.error ?? live.error;

  const workspaceAction = document.getElementById("workspace-view-actions");

  return (
    <Stack spacing={1.5} sx={{ flex: 1, minHeight: 0, overflow: "auto" }}>
      {workspaceAction
        ? createPortal(
            <LiveRunsListenControl
              listen={listen}
              listeningCopy="Capturing live runs and agent logs"
            />,
            workspaceAction
          )
        : null}
      {error ? (
        <IgAlert
          alertKey="agentic-logic-recent-runs-error"
          severity="error"
          title="Could not load recent runs"
          message={error.message}
        />
      ) : null}
      <RecentRunsGrid
        runs={runs}
        loading={history.isPending}
        onRowClick={(run) => setSelected(() => run)}
      />
      <RunDetailDialog
        ruleId={ruleId}
        run={selected}
        onClose={() => setSelected(() => null)}
      />
    </Stack>
  );
};
